<template>
  <div class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Organization' : 'Create New Organization' }}</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Information</h3>
        <div class="form-group two-thirds-width">
          <label for="logo_url">Logo</label>
          <ImageUploadField
            v-model="formData.logo_url"
            folder="distributor_content"
            max-height="100px"
          />
        </div>

        <div class="form-group half-width">
          <label for="name" class="required">Full Legal Name</label>
          <input
            :disabled="isEditing && !isSuperuser()"
            type="text"
            id="name"
            v-model.trim="formData.name"
            class="form-control"
            placeholder="Legal name of the company or organization"
            maxlength="80"
            required
          >
        </div>

        <div class="form-group half-width">
          <label for="registration">VAT/Registration Number</label>
          <input
            type="text"
            id="registration"
            v-model.trim="formData.registration"
            class="form-control"
            maxlength="80"
          >
        </div>

        <div class="form-group half-width">
          <label for="website_url" :class="{ required: !isSuperuser() }">Website URL</label>
          <input
            type="url"
            id="website_url"
            v-model.trim="formData.website_url"
            class="form-control"
            maxlength="255"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group half-width">
          <label for="phone" :class="{ required: !isSuperuser() }">Phone</label>
          <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            class="form-control"
            maxlength="20"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group half-width" v-if="!isEditing && !isSuperuser()">
          <label for="role" class="required">Your Role / Title</label>
          <input
            type="text"
            id="role"
            v-model.trim="formData.role"
            class="form-control"
            placeholder="e.g. Executive Producer"
            maxlength="80"
            required
          />
        </div>

        <div class="form-group full-width">
          <label for="address_line_1" :class="{ required: !isSuperuser() }">Address</label>
          <input
            type="text"
            id="address_line_1"
            v-model.trim="formData.address_line_1"
            class="form-control"
            placeholder="Address Line 1"
            maxlength="255"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group full-width">
          <input
            type="text"
            id="address_line_2"
            v-model.trim="formData.address_line_2"
            class="form-control"
            placeholder="Address Line 2"
            maxlength="255"
          >
        </div>

        <div class="form-group half-width">
          <label for="city" :class="{ required: !isSuperuser() }">City</label>
          <input
            type="text"
            id="city"
            v-model.trim="formData.city"
            class="form-control"
            maxlength="80"
            :required="!isSuperuser()"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="state">State</label>
          <input
            type="text"
            id="state"
            v-model.trim="formData.state"
            class="form-control"
            maxlength="80"
          >
        </div>

        <div class="form-group quarter-width">
          <label for="postal_code">Postal Code / ZIP</label>
          <input
            type="text"
            id="postal_code"
            v-model.trim="formData.postal_code"
            class="form-control"
            maxlength="20"
          >
        </div>

        <div class="form-group half-width">
          <label for="country" :class="{ required: !isSuperuser() }">Country</label>
          <input
            type="text"
            id="country"
            v-model.trim="formData.country"
            class="form-control"
            maxlength="80"
            :required="!isSuperuser()"
          >
        </div>
      </div>

      <OrganizationMembers 
        v-if="isEditing && isSuperuser()"
        :organization-id="organizationId"
      />

      <div class="form-actions">
        <button type="submit" class="submit-button big">
          {{ isEditing ? 'Save Changes' : 'Create Organization' }}
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>

  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { handleApiError } from '@/scripts/shared-utils';
import { getOrganization, createOrganization, updateOrganization, organizationKeys } from '@/scripts/api/organizationApi';
import { userKeys } from '@/scripts/api/userApi';
import { updateUserInfo } from '@/scripts/services/authService';
import { isSuperuser } from '@/scripts/permissions';
import ImageUploadField from '@/components/ImageUploadField.vue';
import OrganizationMembers from '@/components/OrganizationMembers.vue';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const isEditing = computed(() => !!route.params.organization_id);
const organizationId = computed(() => route.params.organization_id);

const formData = ref({
  name: '',
  logo_url: '',
  website_url: '',
  registration: '',
  phone: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  role: '',
});

// Fetch organization data if in edit mode
const organizationQueryKey = computed(() => organizationKeys.getOrganization(organizationId.value));

const { data: organizationData } = useQuery({
  queryKey: organizationQueryKey,
  queryFn: () => getOrganization(organizationId.value),
  enabled: isEditing,
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching organization'),
});

// Watch for fetched data and populate the form
watch(organizationData, (data) => {
  if (data) {
    formData.value = {
      name: data.name || '',
      logo_url: data.logo_url || '',
      website_url: data.website_url || '',
      registration: data.registration || '',
      phone: data.phone || '',
      address_line_1: data.address_line_1 || '',
      address_line_2: data.address_line_2 || '',
      city: data.city || '',
      state: data.state || '',
      country: data.country || '',
      postal_code: data.postal_code || '',
    };
  }
}, { immediate: true });

// Mutation for creating or updating an organization
const saveOrganizationMutation = useMutation({
  mutationFn: (payload) => {
    if (isEditing.value) {
      return updateOrganization(organizationId.value, payload);
    } else {
      return createOrganization(payload);
    }
  },
  onSuccess: async () => {
    if (!isEditing.value) {
      await updateUserInfo();
    }
    // Invalidate related queries, because updateUserInfo() forces the function to be async, await all results
    await queryClient.invalidateQueries({ queryKey: organizationKeys.getAllOrganizations() });
    await queryClient.invalidateQueries({ queryKey: organizationKeys.getAllOrganizationsWithDetails() });
    await queryClient.invalidateQueries({ queryKey: userKeys.getMyOrganizations(), refetchType: 'all' });
    if (isEditing.value) {
      await queryClient.invalidateQueries({ queryKey: organizationKeys.getOrganization(organizationId.value) });
    }
    closeForm();
  },
  onError: (error) => {
    handleApiError(error, 'submitting organization');
    alert('Error saving organization. Please try again.');
  },
});

const handleSubmit = () => {
  let payload = { ...formData.value };
  if (isEditing.value) {
    // Remove role field as it's not part of the update payload
    delete payload.role;
  }
  saveOrganizationMutation.mutate(payload);
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/organizations' : '/organization');
};
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
